import {
  Alert,
  Box,
  Typography,
  useTheme,
  List,
  ListItem,
  ListItemText,
  FormHelperText,
} from '@mui/material';
import {
  objectToCSVByKey,
  useLocalization,
  useUserAccess,
} from '@ui-framework/index';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

function ProfileCompleteness({ status, serviceProviderId, store }) {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const theme = useTheme();
  const userAccess = useUserAccess();
  const warnings = [];

  const isSAOrAdmin = userAccess.isSA() || userAccess.isAdmin();

  if (status?.hasSkills === false) {
    warnings.push({
      label: formatMessage({
        id: 'PROFILE_COMPLETENESS_SKILLS_AT_LEAST_ONE_INDUSTRY_MUST_BE_CONFIGURED',
        defaultMessage:
          'Skills: At least one Industry must be configured to receive any jobs.',
      }),
      onClick: () => {
        navigate(`/serviceproviders/edit/${serviceProviderId}/4`);
      },
    });
  }
  if (status?.hasNotificationEmail === false) {
    warnings.push({
      label: formatMessage({
        id: 'PROFILE_COMPLETENESS_DISPATCH_NOTIFICATIONS_EMAIL_ADDRESS_MUST_BE_SET',
        defaultMessage:
          'Setup: Dispatch Notifications Email address must be set.',
      }),
      onClick: () => {
        navigate(`/serviceproviders/edit/${serviceProviderId}/5`);
      },
    });
  }
  if (status?.hasInsightCallDefault === false) {
    warnings.push({
      label: formatMessage({
        id: 'PROFILE_COMPLETENESS_SETUP_CUSTOMER_COMMUNICATION_DEFAULT_PHONE_NUMBER_NEEDS_TO_BE_SET_FOR_VOICE',
        defaultMessage:
          'Setup: Customer Communication default phone number needs to be set for Voice.',
      }),
      onClick: () => {
        navigate(`/serviceproviders/edit/${serviceProviderId}/5`);
      },
    });
  }
  if (status?.hasInsightSmsDefault === false) {
    warnings.push({
      label: formatMessage({
        id: 'PROFILE_COMPLETENESS_SETUP_CUSTOMER_COMMUNICATION_DEFAULT_PHONE_NUMBER_NEEDS_TO_BE_SET_FOR_SMS_OPT_IN',
        defaultMessage:
          'Setup: Customer Communication default phone number needs to be set for SMS Opt-in.',
      }),
      onClick: () => {
        navigate(`/serviceproviders/edit/${serviceProviderId}/5`);
      },
    });
  }
  if (status?.hasDispatchMethod === false) {
    warnings.push({
      label: formatMessage(
        {
          id: 'PROFILE_COMPLETENESS_DISPATCH_METHOD_SHOULD_TO_BE_SET',
          defaultMessage:
            'System Settings (Dispatch): Dispatch method should to be set. {contactAdminMessage}',
        },
        {
          contactAdminMessage: !isSAOrAdmin
            ? formatMessage({
                id: 'PROFILE_COMPLETENESS_DISPATCH_METHOD_SHOULD_TO_BE_SET_CONTACT_ADMIN_MESSAGE',
                defaultMessage: 'Contact your Service Administrator to update.',
              })
            : '',
        },
      ),
      ...(isSAOrAdmin && {
        onClick: () => {
          navigate(`/serviceproviders/edit/${serviceProviderId}/6/1`);
        },
      }),
    });
  }

  if (status?.isDispatchMethodInactive === true) {
    warnings.push({
      label: formatMessage(
        {
          id: 'PROFILE_COMPLETENESS_DISPATCH_METHOD_IS_INACTIVE',
          defaultMessage:
            'System Settings (Dispatch): Dispatch method is inactive. {contactAdminMessage}',
        },
        {
          contactAdminMessage: !isSAOrAdmin
            ? formatMessage({
                id: 'PROFILE_COMPLETENESS_DISPATCH_METHOD_SHOULD_TO_BE_SET_CONTACT_ADMIN_MESSAGE',
                defaultMessage: 'Contact your Service Administrator to update.',
              })
            : '',
        },
      ),
      ...(isSAOrAdmin && {
        onClick: () => {
          navigate(`/serviceproviders/edit/${serviceProviderId}/6/1`);
        },
      }),
    });
  }

  if (status?.hasServiceTravelDistance === false) {
    warnings.push({
      label: formatMessage({
        id: 'PROFILE_COMPLETENESS_COMPANY_INFO_DEFAULT_SERVICE_TRAVEL_DISTANCE_SHOULD_TO_BE_SET',
        defaultMessage:
          'Company Info: Default Service Travel Distance should to be set.',
      }),
      onClick: () => {
        navigate(`/serviceproviders/edit/${serviceProviderId}/1`);
      },
    });
  }

  if (status?.hasRateCard === false) {
    warnings.push({
      label: formatMessage(
        {
          id: 'PROFILE_COMPLETENESS_CLAIMS_RATE_CARD_SHOULD_BE_SET',
          defaultMessage:
            'System Settings (Claims): Rate card should be set. {contactAdminMessage}',
        },
        {
          contactAdminMessage: !isSAOrAdmin
            ? formatMessage({
                id: 'PROFILE_COMPLETENESS_CLAIMS_RATE_CARD_SHOULD_BE_SET_CONTACT_ADMIN_MESSAGE',
                defaultMessage: 'Contact your Service Administrator to update.',
              })
            : '',
        },
      ),
      ...(isSAOrAdmin && {
        onClick: () => {
          navigate(`/serviceproviders/edit/${serviceProviderId}/6`);
        },
      }),
    });
  }

  if (
    Array.isArray(status?.hasInvalidLocationAddress) &&
    status?.hasInvalidLocationAddress?.length > 0
  ) {
    warnings.push({
      label: formatMessage(
        {
          id: 'PROFILE_COMPLETENESS_LOCATIONS_REQUIRE_REVIEW',
          defaultMessage:
            'Locations: {count} location{plural} require{review} review for receiving jobs',
        },
        {
          count: status?.hasInvalidLocationAddress?.length,
          plural: status?.hasInvalidLocationAddress?.length > 1 ? 's' : '',
          review: status?.hasInvalidLocationAddress?.length === 1 ? '' : 's',
        },
      ),
      onClick: () => {
        const locationNumberString = objectToCSVByKey(
          status?.hasInvalidLocationAddress,
          'locationNumber',
        );
        const filters = [
          {
            field: 'locationNumber',
            operator: 'in',
            value: locationNumberString,
          },
        ];
        // ServiceProviderLocationList is store name from locations list page
        store.domain.list.set('ServiceProviderLocationList', { filters });
        navigate(`/serviceproviders/edit/${serviceProviderId}/3/list`);
      },
    });
  }

  if (warnings?.length === 0) {
    return null;
  }

  return (
    <Alert
      severity="warning"
      sx={{
        mb: 1,
        border: '1px solid',
        borderColor: theme.palette.warning.light,
        pb: 0,
      }}>
      <Box textAlign="left">
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 500 }} gutterBottom>
            {formatMessage({
              id: 'PROFILE_COMPLETENESS_PROFILE_STATUS',
              defaultMessage: 'Profile Status',
            })}
          </Typography>
        </Box>
        <List sx={{ listStyleType: 'disc', pl: 2, pt: 0.1 }}>
          {warnings?.map(item => {
            return (
              <ListItem
                key={item?.label}
                sx={{
                  display: 'list-item',
                  pl: 0,
                  m: 0,
                  py: 0,
                  '&:hover .MuiListItemText-root': {
                    ...(item.onClick && { textDecoration: 'underline' }),
                    ...(item.onClick && { cursor: 'pointer' }),
                  },
                }}>
                <ListItemText
                  {...(item.onClick ? { onClick: item.onClick } : {})}
                  sx={{ fontSize: '0.9em' }}>
                  {item?.label}
                </ListItemText>
              </ListItem>
            );
          })}
        </List>
      </Box>
      <Box>
        <FormHelperText>
          <FormattedMessage
            id="PROFILE_COMPLETENESS_HELP_TEXT"
            defaultMessage="*Click any error to navigate to the section and make corrections."
          />
        </FormHelperText>
      </Box>
    </Alert>
  );
}

export default observer(ProfileCompleteness);
